import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, TrendingUp, Check, X, Store } from "lucide-react";
import { formatMXN } from "@/lib/api";

export default function PriceAlertCard({ alert, onApply, onDismiss, loadingApply }) {
  if (!alert) return null;
  const sample = (alert.affected_sample_names || []).slice(0, 3).join(" · ");

  return (
    <AnimatePresence>
      <motion.div
        key={alert.id}
        initial={{ opacity: 0, y: -20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.98 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative rounded-2xl border border-[#ccff00]/40 bg-gradient-to-br from-[#ccff00]/[0.10] via-[#ccff00]/[0.03] to-transparent p-5 md:p-6 overflow-hidden"
        data-testid={`price-alert-${alert.id}`}
      >
        <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#ccff00]/20 blur-3xl pointer-events-none" />

        <div className="relative flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#ccff00] text-black text-[10px] font-mono uppercase tracking-widest font-bold">
                <Sparkles size={11} /> IA · Alerta de precios
              </span>
              <span className="chip">
                <TrendingUp size={11} className="text-[#ff5a00]" /> +{alert.pct_change}%
              </span>
              <span className="chip">
                <Store size={11} /> Zona {alert.region}
              </span>
            </div>

            <p className="font-display text-xl md:text-2xl tracking-tight leading-snug">
              Se detectó un aumento del{" "}
              <span className="text-[#ff5a00]">{alert.pct_change}%</span>{" "}
              en productos <span className="text-[#ccff00]">{alert.provider}</span> en tu zona.{" "}
              <span className="text-white/60">¿Deseas actualizar tus precios sugeridos?</span>
            </p>

            <p className="mt-2 font-mono text-[11px] text-white/50 uppercase tracking-widest truncate">
              {alert.affected_product_ids.length} producto{alert.affected_product_ids.length !== 1 && "s"} afectado{alert.affected_product_ids.length !== 1 && "s"}
              {sample && <> · {sample}</>}
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 md:min-w-[280px]">
            <button
              onClick={() => onApply?.(alert)}
              disabled={loadingApply}
              className="flex-1 rounded-full bg-[#ccff00] hover:bg-[#dfff40] text-black py-3 px-5 font-bold text-sm flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-60"
              data-testid={`price-alert-apply-${alert.id}`}
            >
              <Check size={16} strokeWidth={3} />
              {loadingApply ? "Actualizando…" : `Actualizar precios (+${alert.pct_change}%)`}
            </button>
            <button
              onClick={() => onDismiss?.(alert)}
              className="rounded-full border border-white/15 text-white/70 hover:text-white hover:bg-white/[0.05] px-4 py-3 text-sm flex items-center justify-center gap-2 transition-colors duration-200"
              data-testid={`price-alert-dismiss-${alert.id}`}
            >
              <X size={14} /> Ignorar
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

export function PriceAlertResultToast({ delta, count }) {
  // Used only via toast content
  return (
    <span>
      Se actualizaron <strong>{count}</strong> productos · +{formatMXN(delta)} en precios sugeridos
    </span>
  );
}
